import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { toast } from "react-toastify";
import { Button, Card, Col, Form, Spinner } from "react-bootstrap";
import { uploadImageToCloudinary as sharedUpload } from "../../utilities/imageUpload";

import {
  createCategory,
  getActiveCategories,
  updateCategory,
} from "../../actions/categoryActions";
import ImageUploadStyledAlert from "views/Components/ImageUploadStyledAlert";
import ArtworkTemplateLink from "../../components/ArtworkTemplateLink";
import { usePermissions } from "../../utilities/usePermissions";
import { dashboardApi } from "../../api/index.js";
import AppSelect from "../../components/ui/AppSelect";

/* Comma-separated text <-> list, for "Other names" and "Keywords". */
const listToText = (list) => (Array.isArray(list) ? list.join(", ") : "");
const textToList = (text) =>
  String(text || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

/* A category name is a pill label. The phone rail fits about 12 characters
   on a line; the server refuses anything over 24. */
const PILL_LENGTH = 12;
const HARD_LENGTH = 24;

/**
 * What a category image actually has to survive.
 * ────────────────────────────────────────────────────────────────────────────
 * These numbers are measured from the two places `category_pic` is rendered,
 * not invented. Re-check them if either changes:
 *
 *   WEB — Lusuku_Web `LandingPage.jsx`: `CategoryPill` is 180px tall and
 *   `ImageContainer` takes 75% of it, so the image box is ~135px tall by the
 *   full card width (4 across on desktop, ~430px). `CategoryImage` is
 *   `object-fit: cover`, so that is a ~3:1 LETTERBOX crop — a square upload
 *   loses most of its height.
 *
 *   APPS — `RetailVendingScreen.js`: the same image renders as a 72x72
 *   CIRCLE, which is a centre-SQUARE crop.
 *
 * The two crop on different axes, which is the whole reason the guidance says
 * "keep the subject centred": whatever sits in the middle is the only part
 * guaranteed to survive both.
 *
 * 1200x675 gives the web card ~2x pixel density at its largest rendered size.
 */
const CATEGORY_IMAGE = {
  idealWidth: 1200,
  idealHeight: 675,
  minWidth: 900,
  minHeight: 506,
  maxMb: 2,
};

const CategoryForm = () => {
  const [postData, setPostData] = useState({
    category: "",
    category_pic: null,
  });
  /* Taxonomy: the group (orders the shop's rail and picks the default
     picture), other names that should still find this category, and the
     words that make the listing recommender suggest it. */
  const [taxonomy, setTaxonomy] = useState({ group: "", aliases: "", keywords: "" });
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    let cancelled = false;
    dashboardApi
      .get("/category/groups")
      .then(({ data }) => {
        if (!cancelled && Array.isArray(data?.groups)) setGroups(data.groups);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const [image, setImage] = useState("");

  const [postListings, setPostListings] = useState({
    listingsAssoc: [],
  });

  const categories = useSelector((state) => state.categories);
  const { category: categoryToEdit, loading } = categories;

  const post_listings = postListings?.listingsAssoc;

  // Alert
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imageDims, setImageDims] = useState(null);
  const [dimensionNote, setDimensionNote] = useState("");




  const dispatch = useDispatch();
  const history = useHistory();
  const { can, loaded: permissionsLoaded } = usePermissions();
  const isEditing = Boolean(categoryToEdit?._id);
  const canManageCategories =
    permissionsLoaded && can("categorization:manage");
  const canSubmitCategory =
    canManageCategories ||
    (permissionsLoaded &&
      can(isEditing ? "categorization:update" : "categorization:create"));

  /* Clear has to reset the IMAGE state too, not just the text field.
     It used to leave `image`, `uploadedFileName` and `uploadDone` behind, so
     after clearing, the form still showed "Category Image Upload Complete!" and
     the next category submitted carried the PREVIOUS category's Cloudinary URL —
     `handleSubmit` reads `image` unconditionally. */
  const clear = () => {
    setPostData({ category: "", category_pic: null });
    setTaxonomy({ group: "", aliases: "", keywords: "" });
    setImage("");
    setUploadedFileName("");
    setIsUploading(false);
    setUploadDone(false);
    setUploadProgress(0);
    setImageDims(null);
    setDimensionNote("");
  };

  useEffect(() => {
    if (categoryToEdit?._id) {
      setPostData({
        category: categoryToEdit.category,
        category_pic: categoryToEdit.category_pic || null,
      });
      setTaxonomy({
        group: categoryToEdit.group || "",
        aliases: listToText(categoryToEdit.aliases),
        keywords: listToText(categoryToEdit.keywords),
      });
      /* Seed `image` with the category's existing picture. Without this it
         starts empty, and `handleSubmit` always writes `category_pic: image` —
         so editing only the NAME of a category silently wiped its artwork.
         A string has no `.name`, so the upload effect below stays dormant until
         the admin actually picks a new file. */
      setImage(categoryToEdit.category_pic || "");
      setUploadedFileName("");
      setUploadDone(false);
      setImageDims(null);
      setDimensionNote("");
      setPostListings({
        listingsAssoc: categoryToEdit.post_listings,
      });
    } else {
      setPostListings({
        listingsAssoc: [],
      });
    }
  }, [categoryToEdit]);


  const fetchImageCategoryToUpload = useCallback(() => {
    return new Promise(async (resolve, reject) => {
      setIsUploading(true);

      try {
        const result = await sharedUpload(image, {
          onProgress: (percent) => setUploadProgress(percent),
        });

        setImage(result.secure_url);
        setIsUploading(false);
        setUploadDone(true);

        const abortController = new AbortController();
        resolve(abortController);
      } catch (err) {
        setIsUploading(false);
        reject(err);
      }
    });
  }, [image]);

  useEffect(() => {
    let active = true;
    let abortControllerPromise;

    if (image?.name) {
      if (active) {
        abortControllerPromise = fetchImageCategoryToUpload();
      }
    }
    return () => {
      active = false;
      if (abortControllerPromise) {
        abortControllerPromise.then((abortController) => {
          abortController.abort();
        });
      }
    };
  }, [image?.name, fetchImageCategoryToUpload]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmitCategory) {
      toast.error("You do not have permission to save categories.");
      return;
    }

    if (image?.name && !uploadDone) {
      await fetchImageCategoryToUpload();
      // const abortController = await fetchImageCategoryToUpload();
      // if (abortController && !abortController.signal.aborted) {
      //   abortController.abort();
      // }
    }
    const categoryData = {
      ...postData,
      category: String(postData.category || "").trim(),
      category_pic: image, // Use the image URL from the `image` state
      post_listings: post_listings,
      group: taxonomy.group || null,
      aliases: textToList(taxonomy.aliases),
      keywords: textToList(taxonomy.keywords).map((k) => k.toLowerCase()),
    };

    if (!categoryToEdit?._id) {
      dispatch(createCategory(categoryData));
      dispatch(getActiveCategories());
      clear();
      history.push("/admin/category-table");
    } else {
      dispatch(updateCategory(categoryToEdit?._id, categoryData));
      dispatch(getActiveCategories()),
        clear(),
        history.push("/admin/category-table");
    }
  };

  // Category Image Upload
  const handleCategoryImageFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Basic validation - the shared utility handles detailed validation
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setSnackbarSeverity("error");
      setSnackbarMessage(
        "Invalid file type. Only JPG, JPEG, and PNG files are allowed."
      );
      setOpenSnackbar(true);
      setUploadedFileName("");
      return;
    }

    /* Measure it, and say so if it will crop badly. NOT a hard block: every
       category already on the platform predates this guidance, and refusing the
       upload would make editing an old category impossible without re-shooting
       its artwork. */
    try {
      const dims = await new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(url);
          resolve({ w: img.naturalWidth, h: img.naturalHeight });
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error("unreadable"));
        };
        img.src = url;
      });
      setImageDims(dims);
      const ratio = dims.w / dims.h;
      if (dims.w < CATEGORY_IMAGE.minWidth || dims.h < CATEGORY_IMAGE.minHeight) {
        setDimensionNote(
          `That image is ${dims.w}x${dims.h}px. Below ${CATEGORY_IMAGE.minWidth}x${CATEGORY_IMAGE.minHeight} it will look soft on the storefront card.`
        );
      } else if (ratio < 1.2) {
        // The web card crops to a wide strip; a square or portrait source loses
        // most of its height there.
        setDimensionNote(
          `That image is ${dims.w}x${dims.h}px (close to square). The storefront card crops to a wide strip, so the top and bottom will be cut off — 16:9 crops far better.`
        );
      } else {
        setDimensionNote("");
      }
    } catch (err) {
      // Unreadable dimensions are not a reason to refuse the file; Cloudinary
      // and the shared validator still get their say.
      setImageDims(null);
      setDimensionNote("");
    }

    // Continue with the upload process
    setImage(file);
    setUploadedFileName(file.name);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  return (
    <>
      {loading ? (
        <Spinner
          animation="grow"
          variant="danger"
          style={{
            marginTop: "70px",
            top: "50%",
            left: "50%",
            position: "fixed",
          }}
        />
      ) : (
        <>
          <ImageUploadStyledAlert
            open={openSnackbar}
            handleClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            message={snackbarMessage}
          />
          <Col>
            <Form id="deliveryZone" autoComplete="off" onSubmit={handleSubmit}>
              <Card>
                <Card.Title className="pt-2 p-2" as="h4">
                  Product Category Form
                </Card.Title>
                <Card.Body className="pt-0">
                  <Col md={6}>
                    <Form.Group>
                      <Card.Body>
                        <label>Category Name</label>
                        {/* Self-closing. `Form.Control` renders a bare <input>,
                            which is a void element — writing it as a container
                            with a body is meaningless, and any child that
                            survived JSX whitespace-stripping would make React
                            throw ("input is a void element tag and must neither
                            have children nor use dangerouslySetInnerHTML"). */}
                        <Form.Control
                          name="category"
                          type="text"
                          maxLength={HARD_LENGTH}
                          placeholder="e.g. Tea & Coffee"
                          value={postData.category}
                          onChange={(e) =>
                            setPostData({
                              ...postData,
                              category: e.target.value,
                            })
                          }
                        />
                        <small
                          style={{
                            display: "block",
                            marginTop: 4,
                            fontSize: 12,
                            color:
                              (postData.category || "").trim().length > PILL_LENGTH
                                ? "#B45309"
                                : "#64748b",
                          }}
                        >
                          {(postData.category || "").trim().length}/{PILL_LENGTH}
                          {(postData.category || "").trim().length > PILL_LENGTH
                            ? ". Longer names are cut off on phone pills; keep the long wording in Other names."
                            : ". Short names fit the shop's pills on every phone."}
                        </small>
                      </Card.Body>
                      <Card.Body className="pt-0">
                        <label>Group</label>
                        <AppSelect
                          block
                          value={taxonomy.group}
                          onChange={(e) => setTaxonomy((t) => ({ ...t, group: e.target.value }))}
                          aria-label="Category group"
                        >
                          <option value="">No group</option>
                          {groups.map((g) => (
                            <option key={g.key} value={g.key}>
                              {g.label}
                            </option>
                          ))}
                        </AppSelect>
                        <small style={{ display: "block", marginTop: 4, fontSize: 12, color: "#64748b" }}>
                          Places this category beside its family in the shop and sets its
                          default picture when none is uploaded.
                        </small>
                      </Card.Body>
                      <Card.Body className="pt-0">
                        <label>Other names</label>
                        <Form.Control
                          name="aliases"
                          type="text"
                          placeholder="e.g. Tea, Coffee"
                          value={taxonomy.aliases}
                          onChange={(e) => setTaxonomy((t) => ({ ...t, aliases: e.target.value }))}
                        />
                        <small style={{ display: "block", marginTop: 4, fontSize: 12, color: "#64748b" }}>
                          Comma separated. Searches, links and imports that use one of these
                          names land here. A renamed category keeps its old name here.
                        </small>
                      </Card.Body>
                      <Card.Body className="pt-0">
                        <label>Keywords</label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          name="keywords"
                          placeholder="e.g. green tea, instant coffee, milo"
                          value={taxonomy.keywords}
                          onChange={(e) => setTaxonomy((t) => ({ ...t, keywords: e.target.value }))}
                        />
                        <small style={{ display: "block", marginTop: 4, fontSize: 12, color: "#64748b" }}>
                          Comma separated. When a product name contains one of these, vendors
                          see this category suggested. Built-in keywords already apply.
                        </small>
                      </Card.Body>
                      <Card.Body className="pt-0">
                        <div style={{ width: "97%", margin: "5px 0" }}>
                          <div>
                            <label
                              htmlFor="category-image-upload"
                              style={{
                                display: "inline-block",
                                padding: "6px 12px",
                                cursor: "pointer",
                                backgroundColor: "#f8f9fa",
                                border: "1px solid #ced4da",
                                borderRadius: "4px",
                                fontWeight: 400,
                                color: "#495057",
                                fontSize: "1rem",
                                lineHeight: "1.5",
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = "#e9ecef";
                                e.target.style.borderColor = "#adb5bd";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = "#f8f9fa";
                                e.target.style.borderColor = "#ced4da";
                              }}
                            >
                              {uploadedFileName || "Upload category image..."}
                            </label>
                            <input
                              id="category-image-upload"
                              name="image"
                              type="file"
                              accept=".jpg, .png, .jpeg"
                              // onChange={(e) =>
                              //   setPostData({ ...postData, image: e.target.files[0] })
                              // }
                              onChange={handleCategoryImageFileChange}
                              style={{ display: "none" }}
                            />
                          </div>

                          {/* Requirements. The two render surfaces crop on
                              different axes, so the guidance has to say more
                              than a single size. */}
                          <div
                            style={{
                              marginTop: 12,
                              padding: "10px 14px",
                              background: "#f5f9ff",
                              border: "1px solid #dbeafe",
                              borderRadius: 8,
                              fontSize: 12.5,
                              color: "#475569",
                              maxWidth: 560,
                            }}
                          >
                            <div style={{ fontWeight: 700, color: "#0a578b", marginBottom: 4 }}>
                              Image requirements
                            </div>
                            <div>
                              <strong>{CATEGORY_IMAGE.idealWidth}x{CATEGORY_IMAGE.idealHeight}px</strong>{" "}
                              (16:9) recommended · minimum {CATEGORY_IMAGE.minWidth}x
                              {CATEGORY_IMAGE.minHeight}px · JPG or PNG up to{" "}
                              {CATEGORY_IMAGE.maxMb} MB.
                            </div>
                            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                              <li>
                                The storefront card crops to a{" "}
                                <strong>wide strip</strong> — the top and bottom
                                of your image are cut off.
                              </li>
                              <li>
                                The apps crop the same image to a{" "}
                                <strong>circle</strong> — the left and right are
                                cut off instead.
                              </li>
                              <li>
                                So keep the subject <strong>centred</strong>, with
                                room around it. Anything near an edge will be lost
                                on one surface or the other.
                              </li>
                            </ul>
                            <ArtworkTemplateLink
                              slotKey="category.image"
                              variant="inline"
                            />
                          </div>

                          {imageDims ? (
                            <p
                              style={{
                                marginTop: 8,
                                fontSize: 12.5,
                                color: dimensionNote ? "#B45309" : "#0f7b6c",
                              }}
                            >
                              Selected: {imageDims.w}x{imageDims.h}px
                              {dimensionNote ? ` — ${dimensionNote}` : " — good fit."}
                            </p>
                          ) : null}
                        </div>
                        <div>
                          {uploadedFileName && (
                            <p
                              style={{
                                fontSize: "13px",
                                fontStyle: "italic",
                              }}
                            >
                              File Name: {uploadedFileName}
                            </p>
                          )}
                          {isUploading && (
                            <p
                              style={{
                                color: "red",
                                fontSize: "13px",
                                fontStyle: "italic",
                              }}
                            >
                              Uploading... {uploadProgress}%
                            </p>
                          )}
                          {uploadDone && !isUploading ? (
                            <p
                              style={{
                                color: "green",
                                fontSize: "13px",
                                fontStyle: "italic",
                              }}
                            >
                              Category Image Upload Complete!
                            </p>
                          ) : null}
                        </div>
                      </Card.Body>
                    </Form.Group>
                  </Col>
                </Card.Body>

                <Card.Body>
                  <Card.Footer className="text-right">
                    <Button
                      className="btn-fill pull-right"
                      variant="danger"
                      onClick={clear}
                      style={{ marginRight: "5px" }}
                    >
                      Clear
                    </Button>
                    {/* `isUploading` matters: submitting mid-upload used to
                        fire a SECOND Cloudinary upload from handleSubmit while
                        the first was still in flight, and whichever resolved
                        last won. Also trim — a name of only spaces is not a
                        name. */}
                    <Button
                      className="btn-fill pull-right"
                      variant="info"
                      type="submit"
                      disabled={
                        !postData.category?.trim() ||
                        !canSubmitCategory ||
                        isUploading
                      }
                    >
                      {isUploading ? "Uploading..." : "Submit"}
                    </Button>
                    <div className="clearfix"></div>
                  </Card.Footer>
                </Card.Body>
              </Card>
            </Form>
          </Col>
        </>
      )}
    </>
  );
};

export default CategoryForm;
